import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth';

/**
 * Confirm a persisted session is still real before acting on it.
 *
 * The auth store persists to localStorage, so `isAuthenticated` can be true
 * long after the token stopped working — expired, revoked, or (in dev) the
 * account deleted out from under it. The signed-out pages redirect away when
 * they see a session, which strands you: signup bounces you to onboarding,
 * onboarding 401s, and you cannot reach either page to fix it.
 *
 * Validating first turns that dead end into a normal signed-out page —
 * validateSession() clears the store when the token is rejected.
 *
 * Returns false until the check settles, so callers can hold off rendering.
 */
export function useValidatedSession(): boolean {
  // Subscribe to the whole store, matching how the auth pages read it.
  const { isAuthenticated } = useAuthStore();
  // Nothing to verify when signed out, so the form paints immediately.
  const [checked, setChecked] = useState(!isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      setChecked(true);
      return;
    }

    let active = true;
    const validate = useAuthStore.getState().validateSession;
    Promise.resolve(validate?.()).finally(() => {
      if (active) setChecked(true);
    });

    return () => {
      active = false;
    };
    // Runs once on mount: a session that goes invalid later is handled by the
    // 401 path in lib/api.ts, not here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return checked;
}
